import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Check, CheckCircle, Loader, Search, UserPlus, X, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import { useDebounce } from '../../../hooks/useDebounce';
import { staffManagerApiService, StaffManagerApiError } from '../../../services/staffManagerApi';
import type { AssignTeamResult, StaffTeam, TesseractUserDetail, TesseractUserSearchRow } from '../../../types/staffManager';
import { EXPIRY_PRESETS, expiryFromPreset, fromDateInputValue, teamEligibility, toDateInputValue } from '../../../utils/staffTenure';
import { PlayerProfile } from '../../PlayerProfile';
import { TeamColourChip } from './TeamColourChip';
import { cn } from '../../../utils/cn';

interface AssignToTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  teams: StaffTeam[];
  /** Preselect a team (opened from a team card). */
  defaultTeamId?: string | null;
  /** Skip the search step (opened from a member profile). */
  presetUser?: { id: string; username: string } | null;
  onSuccess: (result: AssignTeamResult, target: { userId: string; teamId: string }) => void;
}

/**
 * Add a Tesseract user to a staff team. Step 1 finds the user (server-side search, max 10),
 * step 2 checks eligibility against the chosen team before the request is ever sent
 * (PlayCDU teams need Minecraft AND Discord linked), then applies an optional expiry.
 */
export const AssignToTeamModal = ({ isOpen, onClose, teams, defaultTeamId = null, presetUser = null, onSuccess }: AssignToTeamModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [results, setResults] = useState<TesseractUserSearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(presetUser?.id ?? null);
  const [detail, setDetail] = useState<TesseractUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string>(defaultTeamId ?? teams[0]?.id ?? '');
  const [preset, setPreset] = useState<string>('none');
  const [customDate, setCustomDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<AssignTeamResult | null>(null);

  useOnClickOutside(modalRef, onClose);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setSearchError(null);
      setSelectedUserId(presetUser?.id ?? null);
      setDetail(null);
      setDetailError(null);
      setPreset('none');
      setCustomDate('');
      setError(null);
      setSuccess(null);
      setSubmitting(false);
    } else {
      setTeamId(defaultTeamId ?? teams[0]?.id ?? '');
      setSelectedUserId(presetUser?.id ?? null);
    }
  }, [isOpen, defaultTeamId, presetUser?.id, teams]);

  // Search
  useEffect(() => {
    if (!isOpen || presetUser) return;
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setResults([]);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    staffManagerApiService
      .searchUsers(q)
      .then((rows) => {
        if (!cancelled) {
          setResults(rows);
          setSearchError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setSearchError(err instanceof Error ? err.message : 'Search failed');
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, isOpen, presetUser]);

  // Detail (eligibility pre-flight)
  useEffect(() => {
    if (!isOpen || !selectedUserId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    staffManagerApiService
      .getUser(selectedUserId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (!cancelled) setDetailError(err instanceof Error ? err.message : 'Could not load the user');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedUserId]);

  const team = useMemo(() => teams.find((t) => t.id === teamId) ?? null, [teams, teamId]);
  const eligibility = useMemo(() => teamEligibility(detail, team), [detail, team]);
  const alreadyMember = useMemo(() => !!detail && !!team && detail.staff_teams.some((t) => t.id === team.id), [detail, team]);

  const expiresAt = useMemo(() => {
    if (preset === 'custom') return fromDateInputValue(customDate);
    const p = EXPIRY_PRESETS.find((x) => x.id === preset);
    return expiryFromPreset(p?.days ?? null);
  }, [preset, customDate]);
  const expiryInvalid = preset === 'custom' && (expiresAt === null || expiresAt <= Date.now());

  const canSubmit = !!detail && !!team && eligibility.ok && !alreadyMember && !expiryInvalid && !submitting;

  const submit = useCallback(async () => {
    if (!canSubmit || !detail || !team) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await staffManagerApiService.assignToTeam({
        user_id: detail.user.id,
        team_id: team.id,
        ...(expiresAt ? { expires_at: expiresAt } : {}),
      });
      setSuccess(result);
      onSuccess(result, { userId: detail.user.id, teamId: team.id });
    } catch (err) {
      setError(err instanceof StaffManagerApiError ? err.message : err instanceof Error ? err.message : 'Failed to assign');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, detail, team, expiresAt, onSuccess]);

  if (!isOpen) return null;

  const eligibilityRows: { label: string; ok: boolean; detail: string }[] = detail
    ? [
        { label: 'Minecraft linked', ok: !!detail.minecraft, detail: detail.minecraft ? detail.minecraft.username ?? detail.minecraft.uuid : 'not linked' },
        { label: 'Discord linked', ok: !!detail.discord, detail: detail.discord ? `@${detail.discord.username ?? detail.discord.id}` : 'not linked' },
        { label: 'Email verified', ok: detail.user.email_verified, detail: detail.user.email_verified ? 'verified' : 'unverified' },
        { label: 'Account active', ok: !detail.user.disabled && !detail.banned, detail: detail.user.disabled ? 'disabled' : detail.banned ? 'banned' : 'active' },
      ]
    : [];

  const content = (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div ref={modalRef} className="glass-card p-4 md:p-6 rounded-lg w-full max-w-2xl max-md:max-w-[95vw] max-h-[92vh] overflow-y-auto relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-glass-400 hover:text-white touch-manipulation" aria-label="Close">
          <X />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-accent-500/15 text-accent-300">
            <UserPlus className="w-4 h-4" />
          </span>
          <div>
            <h2 className="text-xl font-bold text-white leading-tight">Add to staff team</h2>
            <p className="text-xs text-glass-400">Tesseract enforces your own role.edit permission and the team's linking rules.</p>
          </div>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-green-200">{success.message}</div>
                {success.assigned_groups.length > 0 ? (
                  <p className="text-xs text-green-100/80 mt-1">
                    Groups granted automatically: <span className="font-mono">{success.assigned_groups.join(', ')}</span>
                  </p>
                ) : (
                  <p className="text-xs text-green-100/80 mt-1">This team grants no groups automatically.</p>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="glass-button">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Step 1 — user */}
            <section>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-500 mb-2">1 · Who</div>
              {presetUser ? (
                <div className="glass-panel px-3 py-2 text-sm text-white inline-flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-300" /> @{presetUser.username}
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                    <input
                      autoFocus
                      className="glass-input w-full pl-10"
                      placeholder="Search Tesseract users by username or email…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      aria-label="Search Tesseract users"
                    />
                    {searching && <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 animate-spin" />}
                  </div>
                  {searchError && (
                    <p className="text-xs text-red-300 mt-2 inline-flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> {searchError}
                    </p>
                  )}
                  {results.length > 0 && (
                    <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-glass-200/15 divide-y divide-glass-200/10" role="listbox" aria-label="Matching users">
                      {results.map((u) => {
                        const active = u.id === selectedUserId;
                        return (
                          <li key={u.id}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={active}
                              onClick={() => setSelectedUserId(u.id)}
                              className={cn(
                                'w-full text-left px-3 py-2 flex items-center justify-between gap-3 transition-colors touch-manipulation',
                                active ? 'bg-accent-500/15' : 'hover:bg-glass-200/10',
                              )}
                            >
                              <span className="min-w-0">
                                <span className={cn('block text-sm font-semibold truncate', active ? 'text-accent-200' : 'text-white')}>@{u.username}</span>
                                <span className="block text-[11px] font-mono text-glass-500 truncate">{u.email ?? u.id}</span>
                              </span>
                              <span className="text-[11px] text-glass-500 whitespace-nowrap">
                                {u.disabled ? <span className="text-red-300">disabled</span> : u.created_at ? `joined ${formatDistanceToNow(new Date(u.created_at), { addSuffix: true })}` : ''}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {!searching && debouncedQuery.trim().length >= 2 && results.length === 0 && !searchError && (
                    <p className="text-xs text-glass-500 mt-2">No Tesseract users match “{debouncedQuery.trim()}”.</p>
                  )}
                </>
              )}
            </section>

            {/* Step 2 — team + eligibility */}
            <section>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-500 mb-2">2 · Which team</div>
              <select className="glass-select w-full" value={teamId} onChange={(e) => setTeamId(e.target.value)} aria-label="Team">
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.display_name}
                    {t.domain !== 'playcdu' ? ` (${t.domain})` : ''}
                  </option>
                ))}
              </select>
              {team && team.groups.length > 0 && (
                <p className="text-[11px] text-glass-500 mt-1.5">
                  Grants automatically: <span className="font-mono text-glass-400">{team.groups.map((g) => g.name).join(', ')}</span>
                </p>
              )}

              {selectedUserId && (
                <div className="mt-3 rounded-lg border border-glass-200/15 p-3">
                  {detailLoading ? (
                    <div className="text-xs text-glass-400 inline-flex items-center gap-2">
                      <Loader className="w-3.5 h-3.5 animate-spin" /> Checking eligibility…
                    </div>
                  ) : detailError ? (
                    <p className="text-xs text-red-300 inline-flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> {detailError}
                    </p>
                  ) : detail ? (
                    <>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-white">@{detail.user.username}</span>
                        {detail.minecraft && <PlayerProfile uuid={detail.minecraft.uuid} name={detail.minecraft.username ?? undefined} showUuid={false} inline />}
                        {detail.staff_teams.map((t) => (
                          <TeamColourChip key={t.id} team={t} title="Current team" />
                        ))}
                      </div>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {eligibilityRows.map((row) => (
                          <div key={row.label} className="flex items-center gap-2 text-xs">
                            {row.ok ? <Check className="w-3.5 h-3.5 text-green-300" /> : <XCircle className="w-3.5 h-3.5 text-red-300" />}
                            <dt className="text-glass-400">{row.label}</dt>
                            <dd className={cn('font-mono truncate', row.ok ? 'text-glass-300' : 'text-red-300')}>{row.detail}</dd>
                          </div>
                        ))}
                      </dl>
                      {alreadyMember && (
                        <p className="mt-2 text-xs text-amber-300 inline-flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> Already an active member of {team?.display_name}. Use “Set expiry” instead.
                        </p>
                      )}
                      {!eligibility.ok && (
                        <ul className="mt-2 space-y-0.5">
                          {eligibility.reasons.map((r) => (
                            <li key={r} className="text-xs text-red-300 inline-flex items-center gap-1">
                              <XCircle className="w-3.5 h-3.5 flex-shrink-0" /> {r}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </section>

            {/* Step 3 — expiry */}
            <section>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-500 mb-2">3 · For how long</div>
              <div className="flex flex-wrap items-center gap-2">
                {EXPIRY_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPreset(p.id)}
                    aria-pressed={preset === p.id}
                    className={cn(
                      'h-8 px-3 rounded-md text-xs font-semibold border transition-colors touch-manipulation',
                      preset === p.id ? 'bg-accent-500/20 text-accent-200 border-accent-400/40' : 'border-glass-200/20 text-glass-400 hover:text-white',
                    )}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPreset('custom')}
                  aria-pressed={preset === 'custom'}
                  className={cn(
                    'h-8 px-3 rounded-md text-xs font-semibold border transition-colors touch-manipulation',
                    preset === 'custom' ? 'bg-accent-500/20 text-accent-200 border-accent-400/40' : 'border-glass-200/20 text-glass-400 hover:text-white',
                  )}
                >
                  Pick a date
                </button>
                {preset === 'custom' && (
                  <input
                    type="date"
                    className="glass-input h-8 py-0 text-sm"
                    value={customDate || toDateInputValue(null)}
                    min={toDateInputValue(Date.now() + 86_400_000)}
                    onChange={(e) => setCustomDate(e.target.value)}
                    aria-label="Expiry date"
                  />
                )}
              </div>
              {expiryInvalid && <p className="text-xs text-red-300 mt-1.5">Pick a date in the future.</p>}
              {expiresAt && !expiryInvalid && (
                <p className="text-[11px] text-glass-500 mt-1.5">Membership and its groups expire {formatDistanceToNow(new Date(expiresAt), { addSuffix: true })}.</p>
              )}
            </section>

            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="glass-button-secondary" disabled={submitting}>
                Cancel
              </button>
              <button type="button" onClick={() => void submit()} className="glass-button min-w-[9rem]" disabled={!canSubmit}>
                {submitting ? <Loader className="w-4 h-4 animate-spin" /> : 'Add to team'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
